import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { Colors } from "./components/colors";
import Footer from "./components/footer.jsx";
import Navbar from "./components/navbar.jsx";
import { capitalizeEveryWord } from "./util.jsx";
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  Skeleton
} from '@mui/material';
import { 
  ScatterChart,
  BarChart
} from '@mui/x-charts';
import TrendUpIcon from '@mui/icons-material/TrendingUp';
import WorkIcon from '@mui/icons-material/Work';
import PaidIcon from '@mui/icons-material/Paid';
import { round } from "mathjs";
import RiyalIcon from '../static/images/Saudi_Riyal_Symbol.svg';

const Salaries = () => {
  const { role } = useParams();
  const [salaryData, setSalaryData] = useState({
    scatter: [],
    averages: {
      overall: 0,
      entry: 0,
      mid: 0,
      senior: 0
    },
    distribution: [],
    loading: true
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [role]);

  async function fetchData() {
    try {
      const url = "/api/internal/v1/salary/median?" + 
        new URLSearchParams({
          role: true,
          experience_in_yrs: true
        });

      const response = await fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw Error(response.statusText);
      
      const data = await response.json();
      const filteredData = data.data.filter(salary => salary.role === role);
      
      // Process data for different visualizations
      const scatterData = filteredData.map(salary => ({
        x: salary.experience_in_yrs,
        y: salary.median,
      })).sort((a, b) => a.x - b.x);

      // Create distribution data with ranges
      const distribution = [
        { category: '0-2 Years', value: calculateAverage(filteredData.filter(s => s.experience_in_yrs <= 2)) },
        { category: '3-5 Years', value: calculateAverage(filteredData.filter(s => s.experience_in_yrs > 2 && s.experience_in_yrs <= 5)) },
        { category: '6+ Years', value: calculateAverage(filteredData.filter(s => s.experience_in_yrs > 5)) }
      ];

      setSalaryData({
        scatter: scatterData,
        averages: {
          overall: calculateAverage(filteredData),
        },
        distribution,
        loading: false
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      setSalaryData(prev => ({ ...prev, loading: false }));
    }
  }

  function calculateAverage(data) {
    if (!data.length) return 0;
    return Math.round(data.reduce((acc, curr) => acc + curr.median, 0) / data.length);
  }

  const StatCard = ({ title, value, icon }) => (
    <Card 
      elevation={0} 
      sx={{ 
        height: '100%', 
        border: '1px solid #f0f0f0',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1, fontSize: '0.9rem', color: '#666' }}>
            {title}
          </Typography>
        </Box>
        {salaryData.loading ? (
          <Skeleton variant="text" width="80%" height={40} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={RiyalIcon}
              alt="SAR"
              sx={{
                height: '1.5em',
                width: 'auto',
                marginRight: '6px',
                opacity: 0.9
              }}
            />
            <Typography variant="h4" sx={{ color: '#007646', fontWeight: 600 }}>
              {value !== undefined ? value.toLocaleString() : 'N/A'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const handleReturn = () => {
    window.location.href = "/explore";
  };

  return (
    <>
      <Navbar />
      <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard 
                title="Overall Average" 
                value={salaryData.averages.overall}
                icon={<PaidIcon sx={{ color: '#007646' }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard 
                title="Entry Level Average" 
                value={salaryData.averages.entry}
                icon={<WorkIcon sx={{ color: '#007646' }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard 
                title="Senior Level Average" 
                value={salaryData.averages.senior}
                icon={<TrendUpIcon sx={{ color: '#007646' }} />}
              />
            </Grid>
          </Grid>

          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  border: '1px solid #f0f0f0',
                  borderRadius: 2,
                  height: '100%',
                  minHeight: 500,
                  bgcolor: '#fff'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 4, 
                    color: '#2c3e50',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  Salary by Years of Experience
                </Typography>
                {salaryData.loading ? (
                  <Skeleton variant="rectangular" width="100%" height={400} />
                ) : (
                  <Box sx={{ width: '100%', height: 400, mt: 2 }}>
                    <ScatterChart
                      series={[
                        {
                          data: salaryData.scatter,
                          label: 'Salary Distribution',
                          valueFormatter: (v) => `${v.y.toLocaleString()} ﷼ (${v.x} years)`,
                          color: '#007646'
                        },
                      ]}
                      xAxis={[{ 
                        label: 'Years of Experience',
                        min: 0,
                      }]}
                      yAxis={[{ 
                        label: 'Monthly Salary (SAR)',
                      }]}
                      height={400}
                      margin={{ left: 70, right: 40, top: 20, bottom: 40 }}
                      sx={{
                        '.MuiChartsAxis-line': { stroke: '#ccc' },
                        '.MuiChartsAxis-tick': { stroke: '#ccc' },
                        '.MuiChartsAxis-label': { 
                          fontSize: '0.875rem',
                          fill: '#666'
                        },
                      }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  border: '1px solid #f0f0f0',
                  borderRadius: 2,
                  height: '100%',
                  minHeight: 500,
                  bgcolor: '#fff'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 4, 
                    color: '#2c3e50',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  Salary by Experience Range
                </Typography>
                {salaryData.loading ? (
                  <Skeleton variant="rectangular" width="100%" height={400} />
                ) : (
                  <Box sx={{ width: '100%', height: 400, mt: 2 }}>
                    <BarChart
                      series={[
                        {
                          data: salaryData.distribution.map(d => d.value),
                          label: 'Average Salary',
                          valueFormatter: (v) => `${v.toLocaleString()} ﷼`,
                          color: '#007646'
                        },
                      ]}
                      xAxis={[{ 
                        scaleType: 'band',
                        data: salaryData.distribution.map(d => d.category),
                        tickLabelStyle: {
                          angle: 0,
                          textAnchor: 'middle',
                          fontSize: 12
                        }
                      }]}
                      height={400}
                      margin={{ left: 70, right: 40, top: 20, bottom: 40 }}
                      sx={{
                        '.MuiChartsAxis-line': { stroke: '#ccc' },
                        '.MuiChartsAxis-tick': { stroke: '#ccc' },
                        '.MuiChartsAxis-label': { 
                          fontSize: '0.875rem',
                          fill: '#666'
                        },
                      }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <ReturnButton onClick={handleReturn}>Return To Explore</ReturnButton>
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

const ReturnButton = styled.button`
  position: relative;
  max-width: 200px;
  margin: 0 auto;
  display: block;
  background-color: ${Colors.vividBlue};
  color: ${Colors.white};
  border: none;
  padding: 10px 20px;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${Colors.lightBlue};
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
  }
`;

export default Salaries;
